
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Copy, RotateCw, Key, AlertTriangle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface ApiKeyData {
  key: string;
  server_url: string;
  phone_number: string | null;
}

interface GenerateKeyResponse {
  key_value: string;
  server_url: string;
  phone_number: string | null;
}

export const ApiKeyManagement = () => {
  const [apiKeyData, setApiKeyData] = useState<ApiKeyData | null>(null);
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadApiKey();
  }, []);

  const loadApiKey = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.log("No user found");
        return;
      }

      const { data: keyData, error } = await supabase
        .from('user_api_keys')
        .select('key_value, server_url, phone_number')
        .eq('user_id', user.id)
        .eq('is_active', true)
        .maybeSingle();

      if (error) {
        console.error("Error loading API key:", error);
        return;
      }

      if (keyData) {
        setApiKeyData({
          key: keyData.key_value,
          server_url: keyData.server_url || 'https://njptyhavlbdhmdbvyfdx.supabase.co/functions/v1',
          phone_number: keyData.phone_number
        });
      }
    } catch (error) {
      console.error("Error in loadApiKey:", error);
    }
  };

  const copyApiKey = () => {
    if (!apiKeyData) return;
    
    const fullApiKey = `${apiKeyData.server_url} ${apiKeyData.phone_number || ''} ${apiKeyData.key}`;
    navigator.clipboard.writeText(fullApiKey);
    
    toast({
      title: "API Key copied",
      description: "The API key has been copied to your clipboard",
    });
  };

  const rotateApiKey = async () => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setLoading(false);
        return;
      }

      if (apiKeyData?.key) {
        await supabase
          .from('user_api_keys')
          .update({ is_active: false })
          .eq('key_value', apiKeyData.key);
      }

      const { data: newKeyData, error: functionError } = await supabase
        .rpc('generate_short_jwt', { user_id: user.id }) as { data: GenerateKeyResponse | null, error: any };

      if (functionError) {
        throw functionError;
      }

      if (!newKeyData) {
        throw new Error('No key data received');
      }

      const { error: insertError } = await supabase
        .from('user_api_keys')
        .insert({
          user_id: user.id,
          key_value: newKeyData.key_value,
          server_url: newKeyData.server_url,
          phone_number: newKeyData.phone_number,
          is_active: true,
        });

      if (insertError) {
        throw insertError;
      }

      setApiKeyData({
        key: newKeyData.key_value,
        server_url: newKeyData.server_url,
        phone_number: newKeyData.phone_number
      });

      toast({
        title: "Success",
        description: "API key has been rotated",
      });
    } catch (error: any) {
      console.error("Error rotating API key:", error);
      toast({
        title: "Error",
        description: "Failed to rotate API key: " + error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const formatDisplayKey = () => {
    if (!apiKeyData) return "No API key generated";
    
    const { server_url, phone_number, key } = apiKeyData;
    return `${server_url} ${phone_number || ''} ${"•".repeat(key.length)}`;
  };

  return (
    <Card className="bg-white/90 backdrop-blur-lg border-pink-100">
      <CardHeader className="bg-gradient-to-r from-hotbot-pink to-hotbot-purple text-white">
        <CardTitle className="flex items-center gap-2">
          <Key className="w-5 h-5" />
          API Key
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6 space-y-4">
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Your API key is like a password. Never share it and rotate it if you suspect it has been compromised.
          </AlertDescription>
        </Alert>
        
        <p className="text-gray-600">
          Use your API Key in the <code className="bg-gray-100 px-2 py-1 rounded">x-api-key</code> HTTP
          Header when sending requests to endpoints.
        </p>
        
        <div className="relative">
          <div className="bg-gray-100 p-4 rounded-lg font-mono text-sm break-all">
            {formatDisplayKey()}
          </div>
        </div>

        <div className="flex gap-4">
          <Button
            onClick={copyApiKey}
            className="flex items-center gap-2 bg-blue-500 hover:bg-blue-600"
            disabled={!apiKeyData}
          >
            <Copy className="w-4 h-4" />
            Copy API Key
          </Button>
          
          <Button
            onClick={rotateApiKey}
            className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600"
            disabled={loading}
          >
            <RotateCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            {loading ? "Rotating..." : "Rotate API Key"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
